import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Bell,
  Send,
  Users,
  Car,
  UserCircle,
  History,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  Megaphone,
  Target,
  Clock,
  Calendar,
  Repeat,
  MapPin,
  Ban,
  Timer,
} from "lucide-react";
import { API } from "@/App";

export const NotificationsManagement = ({ adminId, adminRole, assignedCity }) => {
  // Form states
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetAudience, setTargetAudience] = useState("all");
  const [selectedCity, setSelectedCity] = useState("");
  const [scheduleType, setScheduleType] = useState("immediate");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  
  // UI states
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("compose");

  useEffect(() => {
    fetchHistory();
    fetchScheduled();
    fetchStats();
    fetchCities();
  }, [adminId]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/history?admin_id=${adminId}`);
      setHistory(res.data.notifications || []);
    } catch (err) {
      console.error("Failed to fetch notification history:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduled = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/scheduled?admin_id=${adminId}`);
      setScheduled(res.data.scheduled || []);
    } catch (err) {
      console.error("Failed to fetch scheduled notifications:", err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/stats?admin_id=${adminId}`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch notification stats:", err);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/cities?admin_id=${adminId}`);
      // Sort cities alphabetically
      const sortedCities = (res.data.cities || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to fetch cities:", err);
    }
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Please enter both title and message");
      return;
    }

    if (scheduleType === "scheduled") {
      if (!scheduledDate || !scheduledTime) {
        toast.error("Please select date and time for scheduled notification");
        return;
      }
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error("Scheduled time must be in the future");
        return;
      }
    }

    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        target_audience: targetAudience,
        schedule_type: scheduleType,
        recurrence: recurrence,
      };

      // Add city if selected (only for non-city-admin)
      if (selectedCity && selectedCity !== "all" && adminRole !== "city_admin") {
        payload.city_id = selectedCity;
      }

      // Add scheduling info
      if (scheduleType === "scheduled") {
        payload.scheduled_at = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
        if (recurrence !== "none" && recurrenceEndDate) {
          payload.recurrence_end_date = new Date(recurrenceEndDate).toISOString();
        }
      }

      const res = await axios.post(
        `${API}/admin/notifications/broadcast?admin_id=${adminId}`,
        payload
      );

      // Show detailed success message with breakdown
      const data = res.data;
      const expoCount = data.expo_tokens_count || 0;
      const webCount = data.web_subscriptions_count || 0;
      const sentCount = data.sent_count || 0;
      
      let successMsg = data.message || `Notification sent to ${sentCount} user(s)`;
      if (expoCount > 0 || webCount > 0) {
        successMsg = `Notification sent! App: ${expoCount}, PWA/Web: ${webCount}, Total delivered: ${sentCount}`;
      }
      
      toast.success(successMsg);
      resetForm();
      setShowConfirmDialog(false);
      fetchHistory();
      fetchScheduled();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setMessage("");
    setTargetAudience("all");
    setSelectedCity("");
    setScheduleType("immediate");
    setScheduledDate("");
    setScheduledTime("");
    setRecurrence("none");
    setRecurrenceEndDate("");
  };

  const cancelScheduled = async (notificationId) => {
    try {
      await axios.put(`${API}/admin/notifications/${notificationId}/cancel?admin_id=${adminId}`);
      toast.success("Scheduled notification cancelled");
      fetchScheduled();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel notification");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notificationId}?admin_id=${adminId}`);
      toast.success("Notification record deleted");
      fetchHistory();
    } catch (err) {
      toast.error("Failed to delete notification");
    }
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case "all": return "All Users";
      case "customers": return "Customers";
      case "drivers": return "Drivers";
      case "city_admins": return "City Admins";
      default: return audience;
    }
  };

  const getAudienceIcon = (audience) => {
    switch (audience) {
      case "all": return <Users className="w-4 h-4" />;
      case "customers": return <UserCircle className="w-4 h-4" />;
      case "drivers": return <Car className="w-4 h-4" />;
      case "city_admins": return <Users className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getRecurrenceLabel = (rec) => {
    switch (rec) {
      case "none": return "One-time";
      case "daily": return "Daily";
      case "weekly": return "Weekly";
      case "monthly": return "Monthly";
      default: return rec;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Sent</span>;
      case "pending":
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Scheduled</span>;
      case "cancelled":
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">Cancelled</span>;
      case "completed":
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Completed</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">{status}</span>;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_broadcasts || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Broadcasts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_sent || 0}</p>
                  <p className="text-xs text-muted-foreground">Delivered</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.customers_with_push_token || 0}</p>
                  <p className="text-xs text-muted-foreground">Customers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Car className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.drivers_with_push_token || 0}</p>
                  <p className="text-xs text-muted-foreground">Drivers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Compose / Scheduled / History */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="compose" className="gap-2">
            <Bell className="w-4 h-4" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="gap-2">
            <Clock className="w-4 h-4" />
            Scheduled ({scheduled.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Send Push Notification
              </CardTitle>
              <CardDescription>
                Send immediate or schedule notifications for later
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Schedule Type Toggle */}
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    id="schedule-toggle"
                    checked={scheduleType === "scheduled"}
                    onCheckedChange={(checked) => setScheduleType(checked ? "scheduled" : "immediate")}
                  />
                  <Label htmlFor="schedule-toggle" className="cursor-pointer">
                    Schedule for later
                  </Label>
                </div>
                {scheduleType === "scheduled" && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    Will be sent at the scheduled time
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Target Audience */}
                <div className="space-y-2">
                  <Label>Target Audience</Label>
                  <Select value={targetAudience} onValueChange={setTargetAudience}>
                    <SelectTrigger data-testid="notification-audience-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <span className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          All Users (Customers & Drivers)
                        </span>
                      </SelectItem>
                      <SelectItem value="customers">
                        <span className="flex items-center gap-2">
                          <UserCircle className="w-4 h-4" />
                          Customers Only
                        </span>
                      </SelectItem>
                      <SelectItem value="drivers">
                        <span className="flex items-center gap-2">
                          <Car className="w-4 h-4" />
                          Drivers Only
                        </span>
                      </SelectItem>
                      {adminRole !== "city_admin" && (
                        <SelectItem value="city_admins">
                          <span className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            City Admins
                          </span>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Selection (Optional) */}
                {adminRole !== "city_admin" && (
                  <div className="space-y-2">
                    <Label>Target City (Optional)</Label>
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger>
                        <SelectValue placeholder="All cities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          <span className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            All Cities
                          </span>
                        </SelectItem>
                        {cities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            <span className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {city.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Title */}
                <div className="space-y-2">
                  <Label>Notification Title</Label>
                  <Input
                    placeholder="e.g., Special Offer Today!"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={50}
                    data-testid="notification-title-input"
                  />
                  <p className="text-xs text-muted-foreground">{title.length}/50 characters</p>
                </div>

                {/* Scheduled Date/Time */}
                {scheduleType === "scheduled" && (
                  <>
                    <div className="space-y-2">
                      <Label>Schedule Date</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="notification-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Schedule Time</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        data-testid="notification-time-input"
                      />
                    </div>
                  </>
                )}
              </div>
              
              {/* Message */}
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  placeholder="Write your notification message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={200}
                  data-testid="notification-message-input"
                />
                <p className="text-xs text-muted-foreground">{message.length}/200 characters</p>
              </div>

              {/* Recurrence (only for scheduled) */}
              {scheduleType === "scheduled" && (
                <div className="p-4 bg-muted/30 rounded-lg border space-y-4">
                  <div className="flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-primary" />
                    <Label className="font-medium">Recurrence</Label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">Repeat</Label>
                      <Select value={recurrence} onValueChange={setRecurrence}>
                        <SelectTrigger data-testid="notification-recurrence-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">One-time (No repeat)</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {recurrence !== "none" && (
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">End Date (Optional)</Label>
                        <Input
                          type="date"
                          value={recurrenceEndDate}
                          onChange={(e) => setRecurrenceEndDate(e.target.value)}
                          min={scheduledDate || new Date().toISOString().split('T')[0]}
                          placeholder="Never"
                        />
                        <p className="text-xs text-muted-foreground">Leave empty for no end date</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {adminRole === "city_admin" && (
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  <Target className="w-4 h-4 inline mr-2" />
                  This notification will only be sent to users in your assigned city.
                </p>
              )}
              
              <Button
                onClick={() => setShowConfirmDialog(true)}
                disabled={!title.trim() || !message.trim() || sending}
                className="w-full md:w-auto"
                data-testid="send-notification-btn"
              >
                {scheduleType === "immediate" ? (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Now
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Notification
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Tab */}
        <TabsContent value="scheduled">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Scheduled Notifications
                  </CardTitle>
                  <CardDescription>
                    Upcoming notifications waiting to be sent
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchScheduled}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {scheduled.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No scheduled notifications</p>
                  <p className="text-sm text-muted-foreground">Schedule a notification to see it here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduled.map((notif) => (
                    <div
                      key={notif.id}
                      className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                      data-testid={`scheduled-${notif.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-semibold text-sm">{notif.title}</h4>
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                              {getAudienceIcon(notif.target_audience)}
                              {getAudienceLabel(notif.target_audience)}
                            </span>
                            {notif.recurrence && notif.recurrence !== "none" && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                                <Repeat className="w-3 h-3" />
                                {getRecurrenceLabel(notif.recurrence)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{notif.message}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDateTime(notif.next_scheduled_at)}
                            </span>
                            {notif.city_name && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {notif.city_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive border-destructive/30"
                          onClick={() => cancelScheduled(notif.id)}
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-primary" />
                    Notification History
                  </CardTitle>
                  <CardDescription>
                    Previously sent broadcast notifications
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchHistory}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No notifications sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((notif) => (
                    <div
                      key={notif.id}
                      className="p-4 bg-muted/30 rounded-lg border border-border/50"
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-semibold text-sm">{notif.title}</h4>
                            {getStatusBadge(notif.status)}
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                              {getAudienceIcon(notif.target_audience)}
                              {getAudienceLabel(notif.target_audience)}
                            </span>
                            {notif.recurrence && notif.recurrence !== "none" && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                                <Repeat className="w-3 h-3" />
                                {getRecurrenceLabel(notif.recurrence)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{notif.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                            {notif.status === "sent" && (
                              <>
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3 text-green-500" />
                                  {notif.sent_count} sent
                                </span>
                                {notif.failed_count > 0 && (
                                  <span className="flex items-center gap-1">
                                    <XCircle className="w-3 h-3 text-red-500" />
                                    {notif.failed_count} failed
                                  </span>
                                )}
                              </>
                            )}
                            {notif.city_name && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {notif.city_name}
                              </span>
                            )}
                            <span>{formatDateTime(notif.last_sent_at || notif.created_at)}</span>
                          </div>
                        </div>
                        {adminRole !== "city_admin" && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => deleteNotification(notif.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {scheduleType === "immediate" ? "Confirm Broadcast" : "Confirm Schedule"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {scheduleType === "immediate" 
                ? "You are about to send a push notification to:"
                : "You are about to schedule a notification for:"}
            </p>
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {getAudienceIcon(targetAudience)}
                <span className="font-medium">{getAudienceLabel(targetAudience)}</span>
                {selectedCity && selectedCity !== "all" && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {cities.find(c => c.id === selectedCity)?.name || selectedCity}
                  </span>
                )}
              </div>
              {scheduleType === "scheduled" && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>{formatDateTime(`${scheduledDate}T${scheduledTime}`)}</span>
                  {recurrence !== "none" && (
                    <span className="text-muted-foreground">
                      (Repeats {getRecurrenceLabel(recurrence).toLowerCase()})
                    </span>
                  )}
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <p className="font-semibold">{title}</p>
                <p className="text-sm text-muted-foreground">{message}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {scheduleType === "immediate"
                ? "This action cannot be undone. The notification will be sent immediately."
                : "You can cancel this scheduled notification before it's sent."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {scheduleType === "immediate" ? "Sending..." : "Scheduling..."}
                </>
              ) : scheduleType === "immediate" ? (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Now
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsManagement;
